import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, Home, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SchemaOrg } from '@/components/utils/SchemaOrg';

const NotFoundPage = () => {
  const pageTitle = "404: Page Not Found | WebFiny";
  const pageDescription = "Oops! The page you're looking for doesn't exist or has been moved. Let's get you back on track with WebFiny.";

  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": "https://webfiny.com/404", 
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://webfiny.com/404" 
            },
            "publisher": {
              "@type": "Organization",
              "name": "WebFiny",
              "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
              }
            }
          }
        ]}
      />
      <div className="min-h-[calc(100vh-128px)] flex flex-col items-center justify-center text-center px-4 bg-gradient-to-br from-background via-muted/50 to-background">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: -50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 120, damping: 10, delay: 0.1 }}
          className="max-w-md w-full"
        >
          <AlertTriangle className="w-24 h-24 text-destructive mx-auto mb-6 animate-pulse" />
          
          <h1 className="text-6xl md:text-8xl font-extrabold text-primary mb-4 tracking-tighter">
            404
          </h1>
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
            Page Not Found
          </h2>
          <p className="text-muted-foreground mb-8 text-lg leading-relaxed">
            Oops! It seems the page you were looking for doesn't exist or has been moved.
            Don't worry, let's get you back on track.
          </p>

          <div className="mb-8">
            <img
              src="https://images.unsplash.com/photo-1584824486509-112e4181ff6b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Y29uZnVzZWQlMjBwZXJzb258ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=600&h=350&q=80"
              alt="Confused person looking at a map"
              className="rounded-lg shadow-xl mx-auto w-full max-w-sm h-auto object-cover"
              width="600"
              height="350"
             src="https://images.unsplash.com/photo-1594322436404-5a0526db4d13" />
          </div>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-primary/30 transition-all duration-300 transform hover:scale-105 w-full sm:w-auto">
              <Link to="/" aria-label="Go to WebFiny Homepage">
                <Home className="w-5 h-5 mr-2" />
                Go to Homepage
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="border-border hover:bg-muted/80 shadow-lg hover:shadow-muted/30 transition-all duration-300 transform hover:scale-105 w-full sm:w-auto">
              <Link to="/sitemap" aria-label="View WebFiny Sitemap"> 
                <Search className="w-5 h-5 mr-2" />
                Search Site (Sitemap)
              </Link>
            </Button>
          </div>

          <p className="text-sm text-muted-foreground mt-12">
            If you believe this is an error, please <Link to="/contact" className="text-primary hover:underline">contact us</Link>.
          </p>
        </motion.div>
      </div>
    </>
  );
};

export default NotFoundPage;